// @flow
import React, {Component, type Node} from 'react';
import {findDOMNode} from 'react-dom';
import {throttle} from 'lodash';

type Props = {
    onScroll: Function,
    throttleAmount: number,
    children: Node
};

class ScrollListener extends Component<Props> {
    elem: any;
    scrollParent: Element;

    static defaultProps = {
        onScroll: () => {},
        throttleAmount: 100
    };

    componentDidMount() {
        const rawElem = findDOMNode(this);
        this.elem = rawElem;

        // Determine what element (or window) scrolls
        this.scrollParent = this.getScrollParent(this.elem);
        /* istanbul ignore next */
        if (this.elem === this.scrollParent && this.elem instanceof Element) {
            this.elem = this.elem.firstChild;
        }
        this.scrollParent.addEventListener('scroll', this.throttledHandler);
    }

    componentWillUnmount() {
        this.detachListeners();
    }

    /*
        Since scroll events will not always be on the window, we need to find the container that is handling
        the scroll event, and hook up to that.

        This function walks up the tree and finds the containing element that
        should be handling that event.
    */
    getScrollParent(element: any) {
        let style = window.getComputedStyle(element);
        const excludeStaticParent = style.position === 'absolute';
        const overflowRegex = /(auto|scroll)/;

        if (style.position === 'fixed') {
            return window;
        }

        while (element) {
            style = window.getComputedStyle(element);
            if (excludeStaticParent && style.position === 'static') {
                continue;
            }
            if (
                overflowRegex.test(
                    style.overflow + style.overflowY + style.overflowX
                )
            ) {
                return element;
            }
            element = element.parentElement;
        }

        return window;
    }

    detachListeners = () => {
        this.scrollParent.removeEventListener('scroll', this.throttledHandler);
    };

    scrollListener = () => {
        this.props.onScroll();
    };

    throttledHandler = throttle(this.scrollListener, this.props.throttleAmount);

    render() {
        const {children, onScroll, throttleAmount, ...rest} = this.props; // eslint-disable-line no-unused-vars
        return <div {...rest}>{children}</div>;
    }
}

export default ScrollListener;
